import { useCallback, useId, useRef, useState } from "react";

export interface UseFileDropZoneOptions {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}

export interface UseFileDropZoneResult {
  rootProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    "data-dragging": boolean;
  };
  inputProps: {
    ref: React.RefObject<HTMLInputElement | null>;
    type: "file";
    accept: string | undefined;
    multiple: boolean;
    disabled: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    className: string;
  };
  dragging: boolean;
  openPicker: () => void;
}

// Drag-and-drop ignores the input's `accept`; we filter dropped files in JS.
// Accepts a comma-separated list mixing extensions (".pdf"), exact MIME
// types ("image/png"), and wildcards ("image/*"). Empty/undefined → keep all.
export function filterByAccept(files: File[], accept: string | undefined): File[] {
  if (!accept) return files;
  const tokens = accept
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return files;
  return files.filter((file) => {
    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();
    return tokens.some((token) => {
      if (token.startsWith(".")) return name.endsWith(token);
      if (token.endsWith("/*")) {
        const prefix = token.slice(0, -1);
        return type.startsWith(prefix);
      }
      return type === token;
    });
  });
}

export function useFileDropZone({
  accept,
  multiple = false,
  disabled = false,
  onFiles,
}: UseFileDropZoneOptions): UseFileDropZoneResult {
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const deliver = useCallback(
    (files: File[]) => {
      if (disabled || files.length === 0) return;
      const filtered = filterByAccept(files, accept);
      if (filtered.length === 0) return;
      onFiles(multiple ? filtered : filtered.slice(0, 1));
    },
    [accept, disabled, multiple, onFiles],
  );

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current += 1;
      if (e.dataTransfer.types.includes("Files")) setDragging(true);
    },
    [disabled],
  );

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current -= 1;
      if (dragCounter.current <= 0) {
        dragCounter.current = 0;
        setDragging(false);
      }
    },
    [disabled],
  );

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [disabled],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setDragging(false);
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files ?? []);
      deliver(files);
    },
    [disabled, deliver],
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      deliver(files);
      e.target.value = "";
    },
    [deliver],
  );

  const openPicker = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  return {
    rootProps: { onDragEnter, onDragOver, onDragLeave, onDrop, "data-dragging": dragging },
    inputProps: {
      ref: inputRef,
      type: "file",
      accept,
      multiple,
      disabled,
      onChange,
      className: "hidden",
    },
    dragging,
    openPicker,
  };
}

interface FileDropZoneRenderProps {
  openPicker: () => void;
  dragging: boolean;
}

interface FileDropZoneProps {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  className?: string;
  overlayLabel?: string;
  inputAriaLabel?: string;
  children: React.ReactNode | ((props: FileDropZoneRenderProps) => React.ReactNode);
}

export function FileDropZone({
  accept,
  multiple = false,
  disabled = false,
  onFiles,
  className,
  overlayLabel,
  inputAriaLabel,
  children,
}: FileDropZoneProps) {
  const { rootProps, inputProps, dragging, openPicker } = useFileDropZone({
    accept,
    multiple,
    disabled,
    onFiles,
  });
  const overlayId = useId();
  const label = overlayLabel ?? (multiple ? "Drop files here" : "Drop file here");

  return (
    <div {...rootProps} className={`relative ${className ?? ""}`.trim()}>
      <input {...inputProps} aria-label={inputAriaLabel} />
      {typeof children === "function" ? children({ openPicker, dragging }) : children}
      {dragging && (
        <div
          aria-hidden="true"
          data-testid={`file-drop-overlay-${overlayId}`}
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-accent bg-accent/5 text-xs font-medium text-accent"
        >
          {label}
        </div>
      )}
    </div>
  );
}
